import React, { useEffect, useMemo, useState } from "react";
import moment from "moment";
import "moment/locale/fr";
import { Chip, IconButton, Skeleton, Tab, Tabs, Tooltip } from "@mui/material";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import EventBusyOutlinedIcon from "@mui/icons-material/EventBusyOutlined";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import PunctualCourseRepository, { TeachrCourseSessionInfo } from "../../../Repositories/PunctualCourseRepository";
import { useTailwindBrand } from "../../../Components/shared";
import { capitalizeFirstLetter, displayStatutCourse } from "../../../Utils/Function";
import { getActiveClientBrandConfig, getActivePrestationBrandConfig } from "../../../config/environment.config";
import { DetailSection, SectionEmpty } from "./DetailSection";

interface Props {
    teachrId: number;
    clientId: number;
}

type SessionTab = "upcoming" | "past";

/** Lignes affichées avant « Voir les N autres ». */
export const MAX_VISIBLE_SESSIONS = 5;

/** Répartit les séances d'un mois entre à venir (fin après maintenant) et passées, dans l'ordre chronologique. */
export const splitMonthSessions = (sessions: TeachrCourseSessionInfo[], now = moment()) => {
    const sorted = [...sessions].sort((a, b) => moment(a.date).valueOf() - moment(b.date).valueOf());
    return {
        upcoming: sorted.filter((session) => moment(session.endDate).isAfter(now)),
        // les plus récentes d'abord
        past: sorted.filter((session) => !moment(session.endDate).isAfter(now)).reverse(),
    };
};

const formatDuration = (minutes: number) =>
    minutes >= 60
        ? `${Math.floor(minutes / 60)}h${minutes % 60 ? String(minutes % 60).padStart(2, "0") : ""}`
        : `${minutes} min`;

/**
 * Séances du mois du professeur connecté avec ce client : à venir et passées
 * dans le mois affiché (mois en cours par défaut, navigation mois par mois),
 * les séances à venir mises en avant, 5 lignes visibles puis « Voir les N autres ».
 * Lues sur la route des séances du professeur filtrée par client : jamais les
 * séances d'un autre professeur.
 */
const ClientMonthSessionsSection = ({ teachrId, clientId }: Props) => {
    const { brandConfig, classes } = useTailwindBrand();
    const [month, setMonth] = useState(() => moment().startOf("month"));
    const [sessions, setSessions] = useState<TeachrCourseSessionInfo[]>([]);
    const [loading, setLoading] = useState(true);
    const [failed, setFailed] = useState(false);
    const [tab, setTab] = useState<SessionTab | null>(null);
    const [expanded, setExpanded] = useState(false);

    const monthKey = month.format("YYYY-MM");
    const isCurrentMonth = monthKey === moment().format("YYYY-MM");

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setFailed(false);
        setExpanded(false);
        const from = month.format("YYYY-MM-DD");
        const to = month.clone().endOf("month").format("YYYY-MM-DD");
        PunctualCourseRepository.getTeachrCourseSessions(teachrId, from, to, undefined, clientId)
            .then((response) => {
                if (!cancelled) setSessions(response.sessions ?? []);
            })
            .catch(() => {
                if (!cancelled) setFailed(true);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [teachrId, clientId, monthKey]); // eslint-disable-line react-hooks/exhaustive-deps

    const { upcoming, past } = useMemo(() => splitMonthSessions(sessions), [sessions]);

    // Onglet par défaut : les séances à venir dès qu'il y en a, sinon les passées
    const activeTab: SessionTab = tab ?? (upcoming.length > 0 || past.length === 0 ? "upcoming" : "past");
    const rows = activeTab === "upcoming" ? upcoming : past;
    const shown = expanded ? rows : rows.slice(0, MAX_VISIBLE_SESSIONS);
    const hidden = rows.length - shown.length;

    const prestation = getActivePrestationBrandConfig();
    const clientLabel = getActiveClientBrandConfig("", "cet ", "ce ");
    const monthLabel = capitalizeFirstLetter(month.locale("fr").format("MMMM YYYY"));

    // Même pastille que les listes de cours : approbation / rejet d'abord,
    // puis statut URSSAF, sinon « prévu » ou « en cours ».
    const renderStatus = (session: TeachrCourseSessionInfo) => {
        if (session.courseId == null) {
            return (
                <Chip
                    size="small"
                    variant="outlined"
                    label={session.isPunctualMonth ? `Plusieurs ${getActivePrestationBrandConfig("s")}` : capitalizeFirstLetter(prestation)}
                    sx={{ fontSize: "0.68rem", height: 22 }}
                />
            );
        }
        const errorsDesc = session.errorsStatusDescription?.[0]?.description ?? null;
        return displayStatutCourse(
            session.urssafServiceStatus as any,
            session.statusDescription as any,
            errorsDesc as any,
            session.isOver ?? moment(session.endDate).isBefore(moment()),
            false,
            classes,
            session.declarationStatus,
            session.declarationStatusReason
        );
    };

    const changeMonth = (delta: number) => {
        setMonth((current) => current.clone().add(delta, "month").startOf("month"));
        setTab(null);
    };

    return (
        <DetailSection
            title="Séances du mois"
            icon={<EventOutlinedIcon fontSize="small" />}
            subtitle={`Vos séances avec ${clientLabel} en ${monthLabel}, à venir d'abord.`}
            action={
                <div className="flex items-center gap-1">
                    <Tooltip title="Mois précédent">
                        <IconButton size="small" onClick={() => changeMonth(-1)} aria-label="Mois précédent">
                            <ChevronLeftRoundedIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <button
                        type="button"
                        onClick={() => { setMonth(moment().startOf("month")); setTab(null); }}
                        className={`text-xs font-semibold px-2 py-1 rounded-md ${isCurrentMonth ? "text-gray-700 cursor-default" : `${classes.primaryText} hover:underline`}`}
                        disabled={isCurrentMonth}
                    >
                        {monthLabel}
                    </button>
                    <Tooltip title="Mois suivant">
                        <IconButton size="small" onClick={() => changeMonth(1)} aria-label="Mois suivant">
                            <ChevronRightRoundedIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </div>
            }
        >
            <Tabs
                value={activeTab}
                onChange={(_, value: SessionTab) => { setTab(value); setExpanded(false); }}
                variant="fullWidth"
                sx={{
                    minHeight: 36,
                    mb: 1,
                    "& .MuiTab-root": { minHeight: 36, textTransform: "none", fontFamily: brandConfig.fontFamily, fontWeight: 600, fontSize: "0.82rem" },
                    "& .Mui-selected": { color: `${brandConfig.colors.primary} !important` },
                    "& .MuiTabs-indicator": { backgroundColor: brandConfig.colors.primary },
                }}
            >
                <Tab value="upcoming" label={`À venir (${upcoming.length})`} />
                <Tab value="past" label={`Passées (${past.length})`} />
            </Tabs>

            {loading && (
                <div className="space-y-2">
                    {[0, 1, 2].map((i) => <Skeleton key={i} variant="rounded" height={44} />)}
                </div>
            )}

            {!loading && failed && (
                <p className="text-sm text-gray-500">Les séances sont indisponibles pour le moment.</p>
            )}

            {!loading && !failed && rows.length === 0 && (
                <SectionEmpty
                    icon={<EventBusyOutlinedIcon fontSize="large" />}
                    text={activeTab === "upcoming"
                        ? `Aucune séance à venir avec ${clientLabel} en ${monthLabel}.${isCurrentMonth ? ` Utilisez « Déclarer ${getActivePrestationBrandConfig("", "un ", "une ")} » en haut de la fiche pour en ajouter.` : ""}`
                        : `Aucune séance passée avec ${clientLabel} en ${monthLabel}.`}
                />
            )}

            {!loading && !failed && shown.length > 0 && (
                <ul className="divide-y divide-gray-50">
                    {shown.map((session) => {
                        const start = moment(session.date).locale("fr");
                        const end = moment(session.endDate);
                        const isToday = start.isSame(moment(), "day");
                        const isPast = activeTab === "past";
                        return (
                            <li key={`${session.courseId}-${session.date}`} className={`flex items-center gap-3 py-2.5 ${isPast ? "opacity-80" : ""}`}>
                                <div
                                    className="shrink-0 w-12 rounded-lg text-center py-1 leading-tight"
                                    style={{ background: isPast ? "#f3f4f6" : `${brandConfig.colors.primary}14` }}
                                >
                                    <p className={`text-lg font-bold ${isPast ? "text-gray-600" : classes.primaryText}`}>{start.format("DD")}</p>
                                    <p className="text-[10px] uppercase tracking-wide text-gray-500">{start.format("MMM")}</p>
                                </div>
                                <div className="min-w-0 flex-1">
                                    <p className="text-sm font-semibold text-gray-800 truncate">
                                        {isToday ? "Aujourd'hui" : capitalizeFirstLetter(start.format("dddd"))}, {start.format("HH:mm")} à {end.format("HH:mm")}
                                    </p>
                                    <p className="text-xs text-gray-500">
                                        {formatDuration(session.durationMinutes)}
                                        {session.isRemote ? ", à distance" : ""}
                                        {session.isPunctualMonth ? `, plusieurs ${getActivePrestationBrandConfig("s")}` : ""}
                                    </p>
                                </div>
                                {/* Statut du cours de la séance (déclaration, URSSAF), comme dans Mes cours */}
                                {renderStatus(session)}
                            </li>
                        );
                    })}
                    {hidden > 0 && (
                        <li className="pt-2">
                            <button type="button" onClick={() => setExpanded(true)} className={`text-xs font-semibold ${classes.primaryText} hover:underline`}>
                                Voir {hidden === 1 ? "la dernière séance" : `les ${hidden} autres séances`}
                            </button>
                        </li>
                    )}
                    {expanded && rows.length > MAX_VISIBLE_SESSIONS && (
                        <li className="pt-2">
                            <button type="button" onClick={() => setExpanded(false)} className="text-xs font-semibold text-gray-500 hover:underline">
                                Réduire
                            </button>
                        </li>
                    )}
                </ul>
            )}
        </DetailSection>
    );
};

export default ClientMonthSessionsSection;
